import { useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import LoadingIcon from "../../components/LoadingIcon/LoadingIcon";
import styles from './requestSample.module.css';
import { arrow_right, checkIcon } from "../../assets/icons";
import { RequestAlternativeSample } from "../../Models/RequestAlternativeSample";
import { RequestSampleOrQuote } from "../../Api/Services/Alternative";
import ReCAPTCHA from "react-google-recaptcha";

export default function RequestSample() {
    const location = useLocation();
    const { adName, adUrl, contactEmail } = location.state || {};
    const [requestSent, setRequestSent] = useState(false);
    const [loading, setLoading] = useState(false);
    const [invalidName, setInvalidName] = useState(false);
    const [invalidEmail, setInvalidEmail] = useState(false);
    const [invalidphone, setInvalidPhone] = useState(false);
    const [invalidAddress, setInvalidAddress] = useState(false);
    const [invalidCity, setInvalidCity] = useState(false);
    const [invalidRegion, setInvalidRegion] = useState(false);
    const [invalidCountry, setInvalidCountry] = useState(false);
    const [invalidZip, setInvalidZip] = useState(false);
    const [termsChecked, setTermsChecked] = useState(false);
    const [captchaVerified, setCaptchaVerified] = useState(false);
    const [invalidCaptcha, setInvalidCaptcha] = useState(false);
    const nameRef = useRef<HTMLInputElement>(null);
    const emailRef = useRef<HTMLInputElement>(null);
    const phoneRef = useRef<HTMLInputElement>(null);
    const addressRef = useRef<HTMLInputElement>(null);
    const cityRef = useRef<HTMLInputElement>(null);
    const regionRef = useRef<HTMLInputElement>(null);
    const countryRef = useRef<HTMLInputElement>(null);
    const zipRef = useRef<HTMLInputElement>(null);
    const termsRef = useRef<HTMLInputElement>(null);
    const captchaRef = useRef<ReCAPTCHA>(null);
    
    const toggleTermsError = (show: boolean) => {
        if (termsRef.current) {
          termsRef.current.classList.toggle('hidden', !show);
        }
      }
      
    const handleCaptchaChange = (token: string | null) => {
        setCaptchaVerified(!!token);
        if (token) {
            setInvalidCaptcha(false);
        }
    };
    
    const isValidEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email)
    };
    const isValidPhone = (phone: string): boolean => {
        // Supports formats:
        // +1234567890
        // +1 234-567-890
        // 1234567890
        // 123-456-7890
        // (123) 456-7890
        const phoneRegex = /^(\+\d{1,3}\s?)?((\(\d{3}\))|\d{3})[-.\s]?\d{3}[-.\s]?\d{4}$/;
        return phoneRegex.test(phone.trim());
    };

    const isValidZipCode = (zipCode: string, country: string): boolean => {
        // Remove all whitespace
        const cleanZip = zipCode.replace(/\s+/g, '');
        
        const zipRegexes: { [key: string]: RegExp } = {
          // US: 12345 or 12345-6789
          'US': /^\d{5}(-\d{4})?$/,
          // UK: AA9A 9AA, A9A 9AA, A9 9AA, A99 9AA, AA9 9AA, AA99 9AA
          'UK': /^[A-Z]{1,2}[0-9][A-Z0-9]?\s*[0-9][A-Z]{2}$/i,
          // Canada: A1A 1A1
          'CA': /^[ABCEGHJKLMNPRSTVXY]\d[ABCEGHJKLMNPRSTVWXYZ]\s*\d[ABCEGHJKLMNPRSTVWXYZ]\d$/i,
          // Default - accepts 3-10 alphanumeric characters
          'DEFAULT': /^[A-Z0-9]{3,10}$/i
        };
      
        const regex = zipRegexes[country] || zipRegexes['DEFAULT'];
        return regex.test(cleanZip);
    };
    
    const validateForm = () : boolean => {
        let validForm = true;
        let scrollToElement = false;
        if (termsChecked === false) {
            toggleTermsError(true);
            validForm = false;
        }
        if (!captchaVerified) {
            setInvalidCaptcha(true);
            validForm = false;
        }
        const name = nameRef.current;
        if (!name?.value) {
            setInvalidName(true);
            validForm = false;
            name?.scrollIntoView({ behavior: 'smooth' });
            scrollToElement = true;
        }
        const email = emailRef.current;
        if (!email?.value || !isValidEmail(email.value.trim())) {
            // Handle invalid email
            setInvalidEmail(true);
            validForm = false;
            if (!scrollToElement) {
                email?.scrollIntoView({ behavior: 'smooth' });
                scrollToElement = true;
            }
        }
        const phone = phoneRef.current;
        if (!phone?.value || !isValidPhone(phone.value.trim())) {
            setInvalidPhone(true);
            validForm = false;
            if (!scrollToElement) {
                phone?.scrollIntoView({ behavior: 'smooth' });
                scrollToElement = true;
            }
        }
        const address = addressRef.current;
        if (!address?.value) {
            setInvalidAddress(true);
            validForm = false;
            if (!scrollToElement) {
                address?.scrollIntoView({ behavior: 'smooth' });
                scrollToElement = true;
            }
        }
        const city = cityRef.current;
        if (!city?.value) {
            setInvalidCity(true);
            validForm = false;
            if (!scrollToElement) {
                city?.scrollIntoView({ behavior: 'smooth' });
                scrollToElement = true;
            }
        }
        const region = regionRef.current;
        if (!region?.value) {
            setInvalidRegion(true);
            validForm = false;
            if (!scrollToElement) {
                region?.scrollIntoView({ behavior: 'smooth' });
                scrollToElement = true;
            }
        }
        const country = countryRef.current;
        if (!country?.value) {
            setInvalidCountry(true);
            validForm = false;
            if (!scrollToElement) {
                country?.scrollIntoView({ behavior: 'smooth' });
                scrollToElement = true;
            }
        }
        const zip = zipRef.current;
        const countryText = country?.value.toLocaleLowerCase();
        const countryShort = countryText == 'united states' || countryText == 'united states of america' || countryText == 'usa' || countryText == 'us' ? 'US' : countryText == 'united kingdom' || countryText == "england" || countryText == 'uk' ? 'UK' : countryText == 'canada' ? 'CA' : 'DEFAULT';
        if (!zip?.value || !isValidZipCode(zip.value, countryShort)) {
            setInvalidZip(true);
            validForm = false;
            if (!scrollToElement) {
                zip?.scrollIntoView({ behavior: 'smooth' });
                scrollToElement = true;
            }
        }

        return validForm;
    }
    const sendRequest = () => {
        if (!validateForm()) {
            return;
        }
        const data : RequestAlternativeSample = {
            url: window.location.origin + '/alternative' + adUrl,
            fullName: nameRef.current?.value || '',
            email: emailRef.current?.value || '',
            phone: phoneRef.current?.value || '',
            deliveryAddress: addressRef.current?.value || '',
            city: cityRef.current?.value || '',
            region: regionRef.current?.value || '',
            country: countryRef.current?.value || '',
            zipCode: zipRef.current?.value || '',
            samples: parseInt((document.getElementById('samples') as HTMLInputElement).value) || 0,
            volume: parseInt((document.getElementById('annualVolume') as HTMLInputElement).value) || 0,
            volumeUnit: (document.getElementById('annualVolumeUnit') as HTMLSelectElement).value || 'kg',
            message: (document.getElementById('message') as HTMLTextAreaElement).value || '',
            alternativeContactEmail: contactEmail,
            alternativeName: adName,
            recaptchaToken: captchaRef.current?.getValue() || ''
        }
        setLoading(true);
        RequestSampleOrQuote(data).then((response) => {
            if (response.success) {
                setLoading(false);
                setRequestSent(true);
                // Reset captcha
                captchaRef.current?.reset();
                setCaptchaVerified(false);
                document.querySelector('h1')?.scrollIntoView({ behavior: 'smooth' });
            }else{
                setLoading(false);
            }
        });
    }
    return (
        <div className="container">
      { loading && <LoadingIcon /> }
        <div className={styles.content}>

      <Link className={styles.backbutton} to={'/alternative' + adUrl} onClick={() => setLoading(true)}>{arrow_right} Go back to ad</Link>
      <h1 className={styles.title}>Request a quote or sample for:</h1>
      <h2 className={styles.name}>{adName}</h2>
      {requestSent != true &&
      <>
        <form>
            <div className={styles.input_content}>
                <label htmlFor="fullName">Full name*</label>
                <input ref={nameRef} type="text" name="fullName" id="fullName" placeholder="Your full name" className={invalidName ? "error" : ""} onChange={() => setInvalidName(false)}/>
                {invalidName && <p className="error_text">Enter your full name</p>}
                <div>
                    <div>
                        <label htmlFor="email">Contact email*</label>
                        <input ref={emailRef} type="email" name="email" id="email" placeholder="Your contact email" className={invalidEmail ? "error" : ""} onChange={() => setInvalidEmail(false)}/>
                        {invalidEmail && <p className="error_text">Enter a valid email address</p>}
                    </div>
                    <div>
                        <label htmlFor="phone">Contact phone number*</label>
                        <input ref={phoneRef} type="tel" name="phone" id="phone" placeholder="Contact phone number" className={invalidphone ? "error" : ""} onChange={() => setInvalidPhone(false)}/>
                        {invalidphone && <p className="error_text">Enter a valid phone number</p>}
                    </div>
                </div>

                <label htmlFor="address">Delivery address*</label>
                <input ref={addressRef} type="text" name="address" id="address" placeholder="Delivery address" className={invalidAddress ? "error" : ""} onChange={() => setInvalidAddress(false)}/>
                {invalidAddress && <p className="error_text">Enter your delivery address</p>}

                <div>
                    <div>
                        <label htmlFor="city">City*</label>
                        <input ref={cityRef} type="text" name="city" id="city" placeholder="City" className={invalidCity ? "error" : ""} onChange={() => setInvalidCity(false)} />
                        {invalidCity && <p className="error_text">Enter your city</p>}
                    </div>
                    <div>
                        <label htmlFor="region">State, region or provice*</label>
                        <input ref={regionRef} type="text" name="region" id="region" placeholder="State, region or provice" className={invalidRegion ? "error" : ""} onChange={() => setInvalidRegion(false)}/>
                        {invalidRegion && <p className="error_text">Enter your state, region or provice</p>}
                    </div>
                </div>

                <div>
                    <div>
                        <label htmlFor="country">Country*</label>
                        <input ref={countryRef} type="text" name="country" id="country" placeholder="Country" className={invalidCountry ? "error" : ""} onChange={() => setInvalidCountry(false)}/>
                        {invalidCountry && <p className="error_text">Enter your country</p>}
                    </div>
                    <div>
                        <label htmlFor="zip">ZIP code*</label>
                        <input ref={zipRef} type="text" name="zip" id="zip" placeholder="ZIP code" className={invalidZip ? "error" : ""} onChange={() => setInvalidZip(false)} />
                        {invalidZip && <p className="error_text">Enter your ZIP code</p>}
                    </div>
                </div>
                <label htmlFor="samples">Number of samples requested</label>
                <input type="number" name="samples" id="samples" />

                <label htmlFor="annualVolume">Expected annual volume</label>
                <p>If you want to request a quote, please fill the quantity your business will require annually, if the sample product suits your project's needs.
                If you just want to request a sample, leave this empty.</p>
                <div className={styles.annual_volume}>
                    <input type="number" name="annualVolume" id="annualVolume" placeholder="Expected annual volume" />
                    <label htmlFor="annualVolumeUnit" className={styles.hide_lable}>Annual volume unit</label>
                    <select name="annualVolumeUnit" id="annualVolumeUnit" defaultValue={"kg"}>
                        <option value="ton">ton</option>
                        <option value="kg">kg</option>
                        <option value="g">g</option>
                    </select>
                </div>

                <label htmlFor="message">Message to supplier</label>
                <textarea id="message" name="message" rows={4} cols={50} placeholder="Is there anything you would like to ask the supplier? (optional)"></textarea>
            </div>
            <div className={styles.terms}>
                <input type="checkbox" name="terms" id="terms" checked={termsChecked} onChange={() => {setTermsChecked(!termsChecked); toggleTermsError(false)}} />
                <label htmlFor="terms">I have read and I agree to our <Link to="/terms-condition">terms and conditions</Link> and <Link to="/gdpr">GDPR Policy</Link></label>
            </div>
            <p className={"error_text hidden"} ref={termsRef}>Accept terms and conditions to request a quote or sample</p>
            
            <div className={styles.recaptcha_container}>
                <ReCAPTCHA
                    ref={captchaRef}
                    sitekey="6Le0d1QrAAAAAPxy91EWVRboCdZCskhRY1I0rhrw"
                    onChange={handleCaptchaChange}
                />
                {invalidCaptcha && <p className="error_text">Please verify that you are not a robot</p>}
            </div>
            
            <button className={`button button_black ${styles.post}`} type="button" onClick={() => {sendRequest()}}>Send your request</button>
        </form>
      </>
    }
    {requestSent && 
        <div className={styles.posted_container}>
            <div className={styles.checkbox}>
                {checkIcon}
            </div>
            <p>Your request has been sent!</p>
        </div>
    }
    </div>
    </div>
  );
}